import { motion } from 'framer-motion';
import { Eye, Database, Users, FileText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function PrivacyPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);

  return (
    <MarketingLayout>
      <div className="public-breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Privacy' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={<span>Privacy policy</span>}
        title="Your information. Clearly explained."
        subtitle={
          <>
            How we collect, use and protect the information you share with StratPlan.
            <span className="block mt-3 text-sm text-gray-600">
              Last updated: February 13, 2026
            </span>
          </>
        }
      >
        <div className="flex flex-wrap justify-center gap-4">
          <a href="#privacy-policy" className="public-button public-button--secondary">
            Read the policy
          </a>
          <a href="mailto:privacy@stratplan.co" className="public-button public-button--secondary">
            Ask a privacy question
          </a>
        </div>
      </PageHero>

      {/* Trust Section */}
      <section className="public-section public-section--tint">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Database,
                  title: 'Information we collect',
                  description:
                    'The account, planning and usage information covered by this policy.',
                },
                {
                  icon: Eye,
                  title: 'How we use it',
                  description:
                    'The purposes for which we use information and when it may be shared.',
                },
                {
                  icon: Users,
                  title: 'Your choices',
                  description:
                    'Your rights, account choices and how to contact us with a question.',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card text-center p-7"
                >
                  <div className="public-icon w-16 h-16 flex items-center justify-center mx-auto mb-5">
                    <item.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section id="privacy-policy" className="public-section scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="public-prose max-w-4xl mx-auto prose prose-lg">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4 flex items-center">
                  <FileText className="w-8 h-8 mr-3 text-blue-600" />
                  Introduction
                </h2>
                <p className="text-gray-600">
                  StratPlan ("we," "our," or "us") is committed to protecting your privacy. This
                  Privacy Policy explains how we collect, use, disclose, and safeguard your
                  information when you use our strategic planning platform. Please read this policy
                  carefully to understand our practices regarding your information.
                </p>
              </div>

              {/* Information We Collect */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4 flex items-center">
                  <Database className="w-8 h-8 mr-3 text-blue-600" />
                  Information We Collect
                </h2>

                <h3 className="text-2xl font-bold mt-6 mb-3">Information You Provide</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>
                    <strong>Account Information:</strong> Name, email address, password, and company
                    details when you register
                  </li>
                  <li>
                    <strong>Strategic Content:</strong> Vision statements, mission, values,
                    objectives, and other strategic planning data you create
                  </li>
                  <li>
                    <strong>Profile Information:</strong> Job title, role, and other professional
                    details you choose to share
                  </li>
                  <li>
                    <strong>Communication Data:</strong> Messages, feedback, and support requests
                    you send to us
                  </li>
                  <li>
                    <strong>Payment Information:</strong> Billing details processed securely through
                    third-party payment processors
                  </li>
                </ul>

                <h3 className="text-2xl font-bold mt-6 mb-3">
                  Information We Collect Automatically
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>
                    <strong>Usage Data:</strong> How you interact with our platform, features used,
                    and time spent
                  </li>
                  <li>
                    <strong>Device Information:</strong> IP address, browser type, operating system,
                    and device identifiers
                  </li>
                  <li>
                    <strong>Log Data:</strong> Access times, pages viewed, and other diagnostic data
                  </li>
                  <li>
                    <strong>Cookies:</strong> Small data files that help us improve your experience
                    and analyze usage patterns
                  </li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4 flex items-center">
                  <Users className="w-8 h-8 mr-3 text-blue-600" />
                  How We Use Your Information
                </h2>
                <p className="text-gray-600 mb-4">We use your information to:</p>
                <ul className="space-y-2 text-gray-600">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send administrative information, updates, and security alerts</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Generate AI-powered strategic insights and recommendations</li>
                  <li>Monitor and analyze usage patterns to improve user experience</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Comply with legal obligations and enforce our terms</li>
                </ul>
              </div>

              {/* AI and Data Processing */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">AI-Powered Features</h2>
                <p className="text-gray-600 mb-4">
                  StratPlan uses artificial intelligence to provide strategic insights and
                  recommendations. When you use AI features:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>
                    Your strategic content may be processed by AI models to generate suggestions
                  </li>
                  <li>We do not use your data to train general AI models</li>
                  <li>AI-generated content is reviewed and can be edited by you</li>
                  <li>We use industry-leading AI providers with strong privacy commitments</li>
                </ul>
              </div>

              {/* Data Sharing */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Information Sharing and Disclosure</h2>
                <p className="text-gray-600 mb-4">
                  We may share your information in the following situations:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>
                    <strong>With Your Team:</strong> Strategic content you create is accessible to
                    team members you invite
                  </li>
                  <li>
                    <strong>Service Providers:</strong> Third-party vendors who help us operate our
                    platform (hosting, analytics, payment processing)
                  </li>
                  <li>
                    <strong>Legal Requirements:</strong> When required by law or to protect rights,
                    property, or safety
                  </li>
                  <li>
                    <strong>Business Transfers:</strong> In connection with a merger, acquisition,
                    or sale of assets
                  </li>
                  <li>
                    <strong>With Your Consent:</strong> When you explicitly agree to share
                    information
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  <strong>We will never:</strong> Sell your personal information to third parties or
                  use your strategic content for marketing purposes.
                </p>
              </div>

              {/* Data Security */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Data Security</h2>
                <p className="text-gray-600 mb-4">
                  We implement industry-standard security measures to protect your information:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Secure data centers with physical and network security</li>
                  <li>Employee training on data protection practices</li>
                  <li>Incident response procedures and breach notification protocols</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Your Rights and Choices</h2>
                <p className="text-gray-600 mb-4">
                  You have the following rights regarding your information:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>
                    <strong>Access:</strong> Request a copy of your personal information
                  </li>
                  <li>
                    <strong>Correction:</strong> Update or correct inaccurate information
                  </li>
                  <li>
                    <strong>Deletion:</strong> Request deletion of your account and data
                  </li>
                  <li>
                    <strong>Export:</strong> Download your strategic planning data
                  </li>
                  <li>
                    <strong>Opt-Out:</strong> Unsubscribe from marketing communications
                  </li>
                  <li>
                    <strong>Restrict Processing:</strong> Limit how we use your information
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  To exercise these rights, contact us at{' '}
                  <a
                    href="mailto:privacy@stratplan.co"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    privacy@stratplan.co
                  </a>
                </p>
              </div>

              {/* Data Retention */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Data Retention</h2>
                <p className="text-gray-600">
                  We retain your information for as long as your account is active or as needed to
                  provide services. When you delete your account, we will delete or anonymize your
                  information within 30 days, except where we are required to retain it for legal,
                  accounting, or security purposes.
                </p>
              </div>

              {/* International Transfers */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">International Data Transfers</h2>
                <p className="text-gray-600">
                  Your information may be transferred to and processed in countries other than your
                  own. We ensure appropriate safeguards are in place to protect your information in
                  accordance with this Privacy Policy and applicable laws.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Children's Privacy</h2>
                <p className="text-gray-600">
                  StratPlan is not intended for use by children under 16. We do not knowingly
                  collect information from children. If you believe we have inadvertently collected
                  such information, please contact us immediately.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Changes to This Policy</h2>
                <p className="text-gray-600">
                  We may update this Privacy Policy from time to time. We will notify you of
                  material changes by email or through a notice on our platform. Your continued use
                  of StratPlan after changes become effective constitutes acceptance of the updated
                  policy.
                </p>
              </div>

              {/* Contact */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
                <p className="text-gray-600 mb-4">
                  If you have questions about this Privacy Policy or our data practices, please
                  contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Email:</strong>{' '}
                    <a
                      href="mailto:privacy@stratplan.co"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      privacy@stratplan.co
                    </a>
                    <br />
                    <strong>Mail:</strong> StratPlan Privacy Team
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="public-cta">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-4">A clear plan starts with clear information.</h2>
            <p className="text-xl text-gray-600 mb-8">
              Understand the policy, ask us anything you need to know, then build your strategic
              plan.
            </p>
            <Link to="/sign-up" className="public-button public-button--primary">
              Get Started
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
